import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { NeonButton } from "@/components/reelflo/NeonButton";
import { PasswordInput } from "@/components/reelflo/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { nextRouteForUser } from "@/lib/access";

const FIELD_CLASS =
    "bg-white/[0.04] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:border-purple-400/40";

const SOURCES = ["Instagram", "TikTok", "YouTube", "Referral", "Google", "Other"];

const EMPTY = {
    full_name: "",
    email: "",
    phone: "",
    password: "",
    who_are_you: "",
    how_found_us: "",
    why_use: "",
};

export const AuthModal = ({ open, onOpenChange, mode, setMode }) => {
    const { signup, login } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const openerRef = useRef(null);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const handleSignup = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.password) {
            toast.error("Please fill name, email and password.");
            return;
        }
        if (form.password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await signup(form);
            toast.success("Welcome to ReelFlo — let's get you set up.");
            onOpenChange(false);
            setForm(EMPTY);
            // New accounts must subscribe before reaching the dashboard.
            navigate("/subscribe");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not create account.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const loggedIn = await login(form.email, form.password);
            toast.success("Welcome back.");
            onOpenChange(false);
            setForm(EMPTY);
            // Route by access: subscribed users go straight to the dashboard;
            // paid users who still owe a Submagic key go to that step.
            navigate(nextRouteForUser(loggedIn));
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Invalid email or password.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const isSignup = mode === "signup";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="auth-modal"
                onOpenAutoFocus={() => {
                    openerRef.current = document.activeElement;
                }}
                onCloseAutoFocus={(event) => {
                    // Radix only auto-restores focus to a DialogTrigger; this
                    // dialog is opened programmatically, so restore focus to
                    // the real opener here.
                    const el = openerRef.current;
                    if (el && typeof el.focus === "function") {
                        event.preventDefault();
                        try {
                            el.focus();
                        } catch (_e) {
                            /* noop */
                        }
                    }
                }}
                className="max-w-md border-white/10 bg-surface-raised p-0 overflow-hidden sm:rounded-lg"
            >
                <div className="relative max-h-[85vh] overflow-y-auto p-6 sm:p-7">
                    <DialogHeader>
                        <DialogTitle className="rf-heading text-2xl font-bold text-white">
                            {isSignup ? "Create your account" : "Welcome back"}
                        </DialogTitle>
                        <DialogDescription className="text-white/55">
                            {isSignup
                                ? "Join ReelFlo and put your content on autopilot."
                                : "Log in to your creator command center."}
                        </DialogDescription>
                    </DialogHeader>

                    <motion.form
                        key={mode}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        onSubmit={isSignup ? handleSignup : handleLogin}
                        className="mt-5 space-y-4"
                    >
                        {isSignup && (
                            <div className="space-y-1.5">
                                <Label className="text-white/80">Full Name</Label>
                                <Input
                                    data-testid="signup-full-name-input"
                                    className={`${FIELD_CLASS} h-11`}
                                    placeholder="Alex Rivera"
                                    value={form.full_name}
                                    onChange={set("full_name")}
                                />
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label className="text-white/80">Email</Label>
                            <Input
                                data-testid={
                                    isSignup
                                        ? "signup-email-input"
                                        : "login-email-input"
                                }
                                type="email"
                                className={`${FIELD_CLASS} h-11`}
                                placeholder="you@studio.com"
                                value={form.email}
                                onChange={set("email")}
                            />
                        </div>

                        {isSignup && (
                            <div className="space-y-1.5">
                                <Label className="text-white/80">
                                    Phone Number
                                </Label>
                                <Input
                                    data-testid="signup-phone-input"
                                    className={`${FIELD_CLASS} h-11`}
                                    placeholder="+1 555 0100"
                                    value={form.phone}
                                    onChange={set("phone")}
                                />
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label className="text-white/80">Password</Label>
                            <PasswordInput
                                data-testid={
                                    isSignup
                                        ? "signup-password-input"
                                        : "login-password-input"
                                }
                                toggleTestId={
                                    isSignup
                                        ? "signup-password-toggle"
                                        : "login-password-toggle"
                                }
                                className={`${FIELD_CLASS} h-11`}
                                placeholder="••••••••"
                                value={form.password}
                                onChange={set("password")}
                            />
                        </div>

                        {isSignup && (
                            <>
                                <div className="space-y-1.5">
                                    <Label className="text-white/80">
                                        Who are you?
                                    </Label>
                                    <Textarea
                                        data-testid="signup-who-are-you-textarea"
                                        className={FIELD_CLASS}
                                        rows={2}
                                        placeholder="Fitness creator, agency owner, podcaster..."
                                        value={form.who_are_you}
                                        onChange={set("who_are_you")}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <Label className="text-white/80">
                                        How did you find us?
                                    </Label>
                                    <Select
                                        value={form.how_found_us}
                                        onValueChange={set("how_found_us")}
                                    >
                                        <SelectTrigger
                                            data-testid="signup-how-did-you-find-us-select"
                                            className={`${FIELD_CLASS} h-11`}
                                        >
                                            <SelectValue placeholder="Select a source" />
                                        </SelectTrigger>
                                        <SelectContent className="border-white/10 bg-surface-raised text-white">
                                            {SOURCES.map((s) => (
                                                <SelectItem
                                                    key={s}
                                                    value={s}
                                                    className="focus:bg-purple-500/20"
                                                >
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1.5">
                                    <Label className="text-white/80">
                                        Why do you want to use ReelFlo?
                                    </Label>
                                    <Textarea
                                        data-testid="signup-why-use-reelflo-textarea"
                                        className={FIELD_CLASS}
                                        rows={2}
                                        placeholder="I want to post daily without editing for hours."
                                        value={form.why_use}
                                        onChange={set("why_use")}
                                    />
                                </div>
                            </>
                        )}

                        <NeonButton
                            type="submit"
                            size="block"
                            pulse={false}
                            disabled={submitting}
                            data-testid={
                                isSignup
                                    ? "signup-submit-button"
                                    : "login-submit-button"
                            }
                            className="mt-2"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            {isSignup ? "Create account" : "Log in"}
                        </NeonButton>

                        <p className="pt-1 text-center text-sm text-white/55">
                            {isSignup ? (
                                <>
                                    Already have an account?{" "}
                                    <button
                                        type="button"
                                        data-testid="switch-to-login-link"
                                        onClick={() => setMode("login")}
                                        className="inline-flex min-h-11 items-center px-1 font-semibold text-cyan-300 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
                                    >
                                        Log in
                                    </button>
                                </>
                            ) : (
                                <>
                                    New to ReelFlo?{" "}
                                    <button
                                        type="button"
                                        data-testid="switch-to-signup-link"
                                        onClick={() => setMode("signup")}
                                        className="inline-flex min-h-11 items-center px-1 font-semibold text-cyan-300 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
                                    >
                                        Sign up
                                    </button>
                                </>
                            )}
                        </p>
                    </motion.form>
                </div>
            </DialogContent>
        </Dialog>
    );
};
